import React from 'react';
import isEmpty from 'lodash/isEmpty';
import { HomeCard, SourcingBreadcrumb, SourcingHomeTab } from '@/shared/types/sourcing-marketplace';
import Link from 'next/link';
import { CategoryConfig } from '@/shared/types/category';
import Image from 'next/image';
import './index.less';
import { HOMEPAGE_TAB_TO_TEXT } from '@/shared/constants/sourcing-marketplace';
import { useHistoryList } from '@/lib/context/HistoryListProvider';

interface IProps {
  category: CategoryConfig;
  cardInfo: HomeCard;
  tabType: SourcingHomeTab;
  showCardFooter?: boolean;
  index?: number;
}

const CategoryCard = ({ category, cardInfo, tabType, showCardFooter, index }: IProps) => {
  const logos = cardInfo?.logos || [];
  const isPackaging = tabType === SourcingHomeTab.PACKAGING;
  const { setSourcingBreadcrumbs } = useHistoryList();

  const getOverlayPills = () => {
    switch (tabType) {
      case SourcingHomeTab.PRODUCTS: {
        const pills = [];
        if (cardInfo?.factoryCount) {
          pills.push(`${cardInfo?.factoryCount} factories`);
        }
        pills.push('Create custom products');
        return pills;
      }
      case SourcingHomeTab.PACKAGING:
        return ['Create custom packaging'];
      case SourcingHomeTab.BUSINESS_AND_CREATIVE_SERVICES:
      case SourcingHomeTab.WEBSITE_DEVELOPMENT:
        return ['Hire a freelancer'];
      default:
        return [];
    }
  };

  return (
    <Link
      className="category-card"
      href={`/sourcing/browse/${category.key}/${isPackaging ? 'products' : 'suppliers'}`}
      key={category.key}
      onClick={() => {
        setSourcingBreadcrumbs([
          {
            type: 'L1',
            label: HOMEPAGE_TAB_TO_TEXT[tabType],
            L1Key: tabType,
          },
          {
            type: 'L2',
            label: category.title,
            L2Key: category.key,
          },
        ]);
      }}
    >
      <div className="top">
        <div className="text">
          <div className="preheader-container">
            <span className="preheader">{cardInfo.preheader}</span>
          </div>
          <div className="header">
            <h2 className="header-title">{cardInfo.header}</h2>
            {cardInfo?.factoryCount && (
              <span
                className="factory-count seo-data-text-before"
                data-text={cardInfo?.factoryCount + ' factories'}
              ></span>
            )}
          </div>
        </div>
      </div>
      <div className="image-container">
        <Image
          src={category.image}
          sizes="(min-width: 320px) 167px,(min-width: 801px) 310px,(min-width: 1300px) 318px,(min-width: 1800px) 310px, 310px"
          alt={cardInfo.header}
          width={336}
          height={200}
          {...(index === 0 ? { fetchPriority: 'high' } : { loading: 'lazy' })}
        />
        <div className="hover-overlay">
          {getOverlayPills().map((pill: string, i: number) => (
            <span className="pill seo-data-text-before" key={i} data-text={pill}></span>
          ))}
        </div>
      </div>
      {showCardFooter && (
        <>
          {!isEmpty(logos) ? (
            <div className="bottom">
              <span className="title">Factories also work with</span>
              <div className="brand-logos">
                {logos.map((logo: string, i: number) => (
                  <Image
                    src={logo}
                    key={i}
                    alt={cardInfo.header + ' brand logo'}
                    width={57}
                    height={28}
                    {...(index === 0 ? { fetchPriority: 'high' } : { loading: 'lazy' })}
                  />
                ))}
              </div>
            </div>
          ) : (
            <div className="bottom">
              <span className="title seo-data-text-before" data-text="Popular Category:"></span>
              <span className="new-text seo-data-text-before" data-text="Order Samples"></span>
            </div>
          )}
        </>
      )}
    </Link>
  );
};

export default CategoryCard;
